import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage, useAuth } from '../context/AppContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { 
  Stethoscope, Moon, DollarSign, Mic, BookOpen, 
  Dumbbell, Heart, Users, CalendarDays, ChevronRight,
  Zap, ShieldCheck, Sparkles, ArrowRight, Trophy,
  MessageSquare, Footprints, Award, Brain, Radio,
  ShieldCheck as ShieldVerified, Crown
} from 'lucide-react';

const MODULES = [
  {
    id: 'hud_mission',
    icon: Radio,
    gradient: 'from-orange-600/30 to-amber-600/20',
    border: 'border-orange-500/40',
    accent: 'text-orange-400',
    bg: 'bg-orange-500/20',
    title_fr: 'HUD Mission Tracker',
    title_en: 'HUD Mission Tracker',
    subtitle_fr: 'Bandeau "mission-control" en haut du tableau de bord',
    subtitle_en: '"Mission-control" band at the top of the dashboard',
    desc_fr: "Le HUD Mission Tracker est ta première ligne de contact chaque matin. Bandeau horizontal style cockpit avec horloge live, numéro de mission (basé sur ton streak), et compteur des piliers prioritaires restants. La LED pulse en émeraude tant qu'il reste du travail, et bascule en or avec le message « Mission accomplie · Victoire propre » quand tes 3 piliers (Pas, Protéines, Entraînement) sont validés.",
    desc_en: "The HUD Mission Tracker is your first morning contact. Horizontal cockpit-style band with live clock, mission number (based on your streak), and remaining priority pillars counter. LED pulses emerald while work remains, switches to gold with 'Mission complete · Clean win' once all 3 pillars (Steps, Protein, Workout) are validated.",
    faq_fr: [
      { q: "Comment le numéro de mission est-il calculé ?", a: "Mission #N = ton streak actuel (ou ton nombre de jours actifs si pas de streak). À jour 1, c'est Mission #01 ; à 30 jours de streak, c'est Mission #30." },
      { q: "Pourquoi la LED change de couleur ?", a: "Émeraude = il reste au moins 1 pilier à valider aujourd'hui. Or = les 3 piliers validés, mission propre. C'est ton signal visuel immédiat sans avoir à scroller." },
      { q: "Le HUD se met à jour en temps réel ?", a: "Oui. L'horloge se rafraîchit toutes les 30 secondes et le compteur de piliers se met à jour dès que tu logues un repas ou un workout." },
    ],
    faq_en: [
      { q: 'How is the mission number computed?', a: 'Mission #N = your current streak (or active-days fallback). Day 1 = Mission #01, day 30 = Mission #30.' },
      { q: 'Why does the LED change color?', a: 'Emerald = at least 1 pillar still to validate. Gold = all 3 pillars done. Instant visual signal without scrolling.' },
    ],
    action_path: '/dashboard',
    action_label_fr: 'Voir mon HUD',
    action_label_en: 'View my HUD',
    tag_fr: 'NOUVEAU',
    tag_en: 'NEW',
  },
  {
    id: 'triple_victory',
    icon: Trophy,
    gradient: 'from-red-600/30 to-rose-600/20',
    border: 'border-red-500/40',
    accent: 'text-red-400',
    bg: 'bg-red-500/20',
    title_fr: 'Triple Victoire — Rouge Racing',
    title_en: 'Triple Victory — Racing Red',
    subtitle_fr: 'Pas + Protéines + Entraînement — vroom + vibration quand la cible bascule',
    subtitle_en: 'Steps + Protein + Workout — vroom + haptic when target flips',
    desc_fr: "Le widget Triple Victoire centralise tes 3 piliers quotidiens. Les barres affichent un rouge fluo glow tant que tu n'as pas atteint la cible, puis basculent en or/ambre dès que tu valides — accompagnées d'un micro-son « racing » (280 ms) et d'une vibration haptique sur mobile. Quand les 3 sont validés, la carte entière passe en mode « Journée parfaite ! » avec halo or pulsant. Un mute global est disponible (`localStorage.yumix_sfx_muted = '1'`).",
    desc_en: "The Triple Victory widget centralizes your 3 daily pillars. Bars glow fluorescent red until target is hit, then flip to gold/amber — accompanied by a 280ms racing micro-sound and haptic vibration on mobile. When all 3 are hit, the entire card enters 'Perfect day!' mode with pulsing gold halo. Global mute available via localStorage.",
    faq_fr: [
      { q: "Pourquoi un son et une vibration ?", a: "Pour cimenter la boucle dopaminergique. Le cerveau associe le moment de la victoire à un signal multi-sensoriel — beaucoup plus puissant qu'un simple changement visuel. C'est ce qui transforme une habitude fragile en routine ancrée." },
      { q: "Comment couper le son ?", a: "Ouvre la console développeur de ton navigateur et tape : `localStorage.setItem('yumix_sfx_muted', '1')`. Le vroom est désactivé immédiatement, partout dans l'app. La vibration suit le réglage système de ton téléphone." },
      { q: "Le son se déclenche à chaque ouverture du dashboard ?", a: "Non — il se déclenche UNIQUEMENT sur la transition « pas atteint → atteint ». Si tu arrives avec une cible déjà validée, aucun son. C'est la magie du sentinel `prev.current` dans le hook `useVictoryFx`." },
    ],
    faq_en: [
      { q: 'Why sound and vibration?', a: 'To cement the dopamine loop. The brain associates victory with a multi-sensory signal — far stronger than a visual change.' },
      { q: 'How to mute?', a: "Run `localStorage.setItem('yumix_sfx_muted', '1')` in dev console. Vibration follows your phone's system settings." },
    ],
    action_path: '/dashboard',
    action_label_fr: 'Voir ma Triple Victoire',
    action_label_en: 'View my Triple Victory',
    tag_fr: 'NOUVEAU',
    tag_en: 'NEW',
  },
  {
    id: 'mindset_ai',
    icon: Brain,
    gradient: 'from-violet-600/30 to-fuchsia-600/20',
    border: 'border-violet-500/40',
    accent: 'text-violet-400',
    bg: 'bg-violet-500/20',
    title_fr: 'Mindset OS — IA proactive Gemini 3',
    title_en: 'Mindset OS — Proactive Gemini 3 AI',
    subtitle_fr: 'Recap quotidien + hebdo + mensuel de ton OS mental',
    subtitle_en: 'Daily + weekly + monthly recap of your mental OS',
    desc_fr: "Mindset OS est ton 4ème pilier — celui que la plupart des apps ignorent. Chaque matin, une bannière proactive te demande comment tu te sens (énergie, stress, focus, gratitude). Gemini 3 Flash analyse en arrière-plan ton historique et te livre instantanément un feedback de coach personnalisé en 3 phrases. À la fin de chaque semaine et de chaque mois, un recap IA résume l'évolution de ton mental avec des cartes glowing — comme si un coach de haut niveau te débrieffait.",
    desc_en: "Mindset OS is your 4th pillar — the one most apps ignore. Each morning, a proactive banner asks how you feel (energy, stress, focus, gratitude). Gemini 3 Flash analyzes your history in the background and instantly delivers personalized 3-sentence coach feedback. Weekly and monthly recaps summarize your mental evolution.",
    faq_fr: [
      { q: "C'est juste un journal ?", a: "Non. C'est un système actif. L'IA détecte les patterns (3 jours de stress consécutifs → suggestion concrète, énergie en baisse → check protéines/sommeil) et te débriefe comme un vrai coach. Le journal classique est passif, Mindset OS est proactif." },
      { q: "Pourquoi Gemini 3 Flash et pas GPT ?", a: "Gemini 3 Flash est ultra-rapide (sous 800 ms en moyenne) et excellent sur les analyses contextuelles courtes. Le prefetch en arrière-plan permet d'avoir le feedback affiché AVANT même que tu finisses de lire la bannière." },
      { q: "Mes données sont-elles privées ?", a: "Oui. L'IA voit uniquement ton historique mindset (énergie/stress/focus/gratitude par date). Pas de partage, pas d'entraînement de modèles tiers. Le contexte est passé en clear à chaque appel et oublié immédiatement." },
    ],
    faq_en: [
      { q: 'Is it just a journal?', a: 'No. Active system: the AI detects patterns (3 stress days → concrete suggestion, energy dropping → check protein/sleep) and debriefs like a real coach.' },
      { q: 'Why Gemini 3 Flash?', a: 'Sub-800ms latency and excellent on short contextual analyses. Background prefetch displays feedback BEFORE you finish reading the banner.' },
    ],
    action_path: '/dashboard',
    action_label_fr: 'Voir mon Mindset',
    action_label_en: 'View my Mindset',
    tag_fr: 'NOUVEAU',
    tag_en: 'NEW',
  },
  {
    id: 'fatsecret_precision',
    icon: ShieldVerified,
    gradient: 'from-cyan-600/30 to-sky-600/20',
    border: 'border-cyan-500/40',
    accent: 'text-cyan-400',
    bg: 'bg-cyan-500/20',
    title_fr: 'FatSecret Vérifié + Top Précision',
    title_en: 'FatSecret Verified + Top Precision',
    subtitle_fr: 'Macros validées par la base FatSecret Canada + classement précision',
    subtitle_en: 'Macros validated by FatSecret Canada DB + precision leaderboard',
    desc_fr: "Quand tu logues un repas, YUMIX tente d'abord de trouver l'aliment dans la base FatSecret Canada (vraies données nutrition validées). Si trouvé, ton repas affiche un badge cyan « VÉRIFIÉ » et compte dans ta stat de précision. Sinon, fallback IA transparent (estimation Gemini). Le leaderboard « Top Précision 🇨🇦 » classe les utilisateurs selon leur pourcentage de repas vérifiés — pousse vers la rigueur sans tomber dans l'obsession.",
    desc_en: "When you log a meal, YUMIX first tries the FatSecret Canada DB (validated nutrition data). If found, your meal displays a cyan 'VERIFIED' badge and counts toward your precision stat. Otherwise, transparent AI fallback (Gemini estimate). The 'Top Precision 🇨🇦' leaderboard ranks users by their verified-meals percentage.",
    faq_fr: [
      { q: "Pourquoi seulement Canada pour l'instant ?", a: "Notre clé API FatSecret est en tier gratuit avec whitelist d'IP. Une fois la propagation effective, on étendra à US/EU. En attendant, le fallback IA garantit que l'app fonctionne pour tout le monde." },
      { q: "Mon repas est-il moins fiable s'il n'est pas vérifié ?", a: "Pas vraiment — Gemini est très solide sur les macros standards (poulet, riz, légumes). FatSecret apporte juste une couche de validation supplémentaire pour les produits emballés (codes-barres, marques). Le badge cyan signale juste « source officielle »." },
      { q: "Comment monter dans le Top Précision ?", a: "Logue le maximum de tes repas avec le nom EXACT du produit (ex: « Yogourt grec Liberté 0% 175g » plutôt que « yogourt »). Plus le nom est précis, plus FatSecret matche, plus ton % monte." },
    ],
    faq_en: [
      { q: 'Why Canada only for now?', a: 'Our FatSecret API key is free-tier with IP whitelisting. Once propagation completes, we expand to US/EU.' },
      { q: 'How to climb the Top Precision?', a: "Log meals with EXACT product names (e.g., 'Liberté Greek 0% 175g' vs just 'yogurt'). More precise = more FatSecret matches." },
    ],
    action_path: '/leaderboard',
    action_label_fr: 'Voir le Top Précision',
    action_label_en: 'View Top Precision',
    tag_fr: 'VÉRIFIÉ',
    tag_en: 'VERIFIED',
  },
  {
    id: 'ai_assistant',
    icon: MessageSquare,
    gradient: 'from-orange-600/30 to-red-600/20',
    border: 'border-orange-500/40',
    accent: 'text-orange-400',
    bg: 'bg-orange-500/20',
    title_fr: 'Assistant IA YUMIX (Friction Zéro)',
    title_en: 'YUMIX AI Assistant (Zero Friction)',
    subtitle_fr: 'Parle-lui, il fait le travail. Plus besoin de chercher dans les menus.',
    subtitle_en: "Talk to it, it does the work. No more hunting through menus.",
    desc_fr: "L'Assistant IA est ton interface conversationnelle : il repond a tes questions nutritionnelles, il cartographie toute l'app, et surtout il genere des BOUTONS D'ACTION qui te redirigent vers la bonne section avec les champs DEJA PRE-REMPLIS. Exemple : dis-lui 'Je pese 184.5 lbs', et un bouton orange apparait : 'Enregistrer 184.5 lbs'. 1 clic pour confirmer au lieu de 5 etapes manuelles. Il respecte aussi tous tes modes (jeune, paleo, keto, vegan, allergies, clinique).",
    desc_en: "The AI Assistant is your conversational interface: answers nutrition questions, maps the entire app, and generates ACTION BUTTONS that redirect you with fields ALREADY PRE-FILLED. Example: say 'I weigh 184.5 lbs' and an orange button appears: 'Save 184.5 lbs'. 1 click vs 5 steps. Respects all your modes (fasting, paleo, keto, vegan, allergies, clinical).",
    faq_fr: [
      { q: 'Comment mettre a jour mon poids en 2 clics ?', a: "Dis-lui 'Mets a jour mon poids a 185 lbs'. Il generera un bouton oriange 'Enregistrer 185 lbs'. Clic → page Mon Evolution s'ouvre avec 185 deja entre. Clic 'Confirmer' → enregistre." },
      { q: 'Je suis en jeune, peut-il me suggerer quand manger ?', a: "Oui. L'IA connait ton statut de jeune (heure de fin) et formulera ses suggestions pour TA RUPTURE DE JEUNE, jamais 'mange maintenant' pendant la fenetre de jeune." },
      { q: "Puis-je lui demander 'Ou je vois mes badges' ?", a: "Oui. Il repondra avec un bouton direct vers 'Mes Badges'. Il connait TOUTES les sections : Evolution, Pas, Jeune, Repas, Hydratation, Supplements, Workouts, Parametres, etc." },
      { q: "Est-ce securise ?", a: "Oui. Une allowlist de routes + parametres par route bloque toute redirection malveillante. L'IA ne peut jamais t'envoyer hors de l'app." },
    ],
    faq_en: [
      { q: 'How do I update my weight in 2 clicks?', a: "Say 'Update my weight to 185 lbs'. It generates an orange button 'Save 185 lbs'. Tap → Evolution page opens pre-filled. Tap 'Confirm' → done." },
      { q: "I'm fasting, can it suggest when to eat?", a: "Yes. The AI knows your fasting status (end time) and phrases suggestions for YOUR FAST BREAK, never 'eat now' during the fasting window." },
    ],
    action_path: '/coach-ai',
    action_label_fr: 'Parler a l\'Assistant IA',
    action_label_en: 'Talk to the AI Assistant',
    tag_fr: 'NOUVEAU',
    tag_en: 'NEW',
  },
  {
    id: 'walking_command',
    icon: Footprints,
    gradient: 'from-cyan-600/30 to-blue-600/20',
    border: 'border-cyan-500/40',
    accent: 'text-cyan-400',
    bg: 'bg-cyan-500/20',
    title_fr: 'Centre de Commandement Marche',
    title_en: 'Walking Command Center',
    subtitle_fr: '22 badges tactiques + jauge 240px + historique semaine/mois',
    subtitle_en: '22 tactical badges + 240px gauge + weekly/monthly history',
    desc_fr: "Ton QG de marche. Depuis le tableau de bord, tape sur le widget Pas pour acceder au Centre de Commandement : objectif quotidien, jauge circulaire geante, graphiques hebdo et mensuel, historique des 12 derniers mois, et surtout les 22 badges tactiques (Cadet de la Marche, Infanterie Legere, L'Avant-Garde avant midi, Operation Commando 15k pas, Pont de Quebec, Traversee du Canada, Operation Apollo 500M pas lifetime, etc.). Chaque log de pas verifie automatiquement les 22 criteres et decerne les badges au bon moment.",
    desc_en: "Your walking HQ. From the dashboard, tap the Steps widget to access the Command Center: daily goal, giant circular gauge, weekly/monthly charts, 12-month history, and especially the 22 tactical badges. Each step log automatically evaluates all 22 rules.",
    faq_fr: [
      { q: "Combien y a-t-il de badges de marche ?", a: "22 badges marche. Plus 5 Carburant + 4 Forge + 3 Citadelle + 5 Grades = 39 badges YUMIX au total." },
      { q: "Quel est le badge le plus dur ?", a: "Operation Apollo : 500 MILLIONS de pas cumules (legendaire). Pour reference, la Traversee du Canada c'est 10 millions." },
      { q: "Y a-t-il un widget 'Prochain Badge' ?", a: "Oui. Il cycle automatiquement sur les 3 badges les plus proches du deblocage, toutes categories confondues. Clic → redirige vers /walking." },
    ],
    faq_en: [
      { q: 'How many walking badges?', a: "22 walking badges. Plus 5 Fuel + 4 Forge + 3 Citadel + 5 Ranks = 39 YUMIX badges total." },
      { q: 'Hardest badge?', a: "Operation Apollo: 500 MILLION lifetime steps (legendary). The Canada Crossing is 'only' 10M." },
    ],
    action_path: '/walking',
    action_label_fr: 'Ouvrir le Centre de Commandement',
    action_label_en: 'Open Walking Command',
    tag_fr: 'NOUVEAU',
    tag_en: 'NEW',
  },
  {
    id: 'badges_hub',
    icon: Award,
    gradient: 'from-yellow-600/30 to-amber-600/20',
    border: 'border-yellow-500/40',
    accent: 'text-yellow-400',
    bg: 'bg-yellow-500/20',
    title_fr: 'Mes Badges — 39 trophees tactiques',
    title_en: 'My Badges — 39 tactical trophies',
    subtitle_fr: '6 onglets : Tous / Marche / Forge / Carburant / Citadelle / Grades',
    subtitle_en: '6 tabs: All / Walking / Forge / Fuel / Citadel / Ranks',
    desc_fr: "La page 'Mes Badges' (anciennement Victoires) centralise les 39 badges YUMIX en 6 categories filtrables. Chaque badge debloque affiche la date d'obtention et peut etre partage en une image OG 1200x630 generee automatiquement pour Facebook/Instagram/Messenger.",
    desc_en: "The 'My Badges' page centralizes all 39 YUMIX badges in 6 filterable categories. Each unlocked badge shows its date and can be shared as an auto-generated 1200x630 OG image for Facebook/Instagram/Messenger.",
    faq_fr: [
      { q: "Comment partager un badge ?", a: "Ouvre 'Mes Badges', clic sur un badge debloque, bouton 'Partager'. Une image OG est generee avec ton nom + icone + date. Prete pour Facebook/Instagram." },
      { q: "Ou voir mes progres vers les badges pas encore obtenus ?", a: "Le widget 'Prochain Badge' sur le tableau de bord cycle sur les 3 badges les plus proches. Pour la liste complete, la section 'Centre de Commandement Marche' affiche la progression de chaque badge marche." },
    ],
    faq_en: [
      { q: "Share a badge?", a: "Open 'My Badges', tap an unlocked badge, hit 'Share'. An OG image is auto-generated with your name + icon + date." },
    ],
    action_path: '/victories',
    action_label_fr: 'Voir mes badges',
    action_label_en: 'View my badges',
    tag_fr: 'NOUVEAU',
    tag_en: 'NEW',
  },
  {
    id: 'clinical',
    icon: Stethoscope,
    gradient: 'from-emerald-600/30 to-teal-600/20',
    border: 'border-emerald-500/40',
    accent: 'text-emerald-400',
    bg: 'bg-emerald-500/20',
    title_fr: 'Module Clinique',
    title_en: 'Clinical Module',
    subtitle_fr: 'Gestion des blessures et protocoles de récupération',
    subtitle_en: 'Injury management and recovery protocols',
    desc_fr: 'Active le mode clinique pour que YUMIX adapté ses conseils si tu es blessé, en convalescence ou en récupération. L\'assistant ajustera automatiquement tes calories (baisse puisque l\'activité diminue) et augmentera les nutriments de réparation : collagène, oméga-3, zinc, vitamine C et antioxydants.',
    desc_en: 'Enable clinical mode so YUMIX adapts advice for injury or recovery. It\'ll adjust calories and boost repair nutrients: collagen, omega-3, zinc, vitamin C and antioxidants.',
    faq_fr: [
      { q: 'Je suis blessé, que faire ?', a: 'Active le Mode Blessure dans tes réglages. YUMIX réduira tes calories totales (puisque tu bouges moins) mais augmentera stratégiquement les protéines et nutriments anti-inflammatoires. Tu verras des suggestions de repas riches en collagène (bouillon d\'os), oméga-3 (saumon, sardines) et antioxydants (bleuets, curcuma).' },
      { q: 'Combien de temps garder le mode actif ?', a: 'Garde-le actif pendant toute ta période de convalescence. YUMIX ajustera progressivement les recommandations au fur et à mesure de ta récupération.' },
    ],
    faq_en: [
      { q: 'I\'m injured, what should I do?', a: 'Enable Injury Mode in your settings. YUMIX will reduce total calories while strategically increasing protein and anti-inflammatory nutrients.' },
    ],
    action_path: '/settings',
    action_label_fr: 'Activer le mode clinique',
    action_label_en: 'Enable clinical mode',
    tag_fr: 'Récupération',
    tag_en: 'Recovery',
  },
  {
    id: 'hormonal',
    icon: Heart,
    gradient: 'from-pink-600/30 to-rose-600/20',
    border: 'border-pink-500/40',
    accent: 'text-pink-400',
    bg: 'bg-pink-500/20',
    title_fr: 'Santé Hormonale',
    title_en: 'Hormonal Health',
    subtitle_fr: 'Optimisation métabolique : insuline, cortisol, cycle hormonal',
    subtitle_en: 'Metabolic optimization: insulin, cortisol, hormonal cycle',
    desc_fr: 'YUMIX intègre le cycle hormonal dans ses conseils nutritionnels. L\'assistant adapté les macros selon les phases du cycle menstruel (phase folliculaire vs lutéale), optimisé la gestion de l\'insuline et du cortisol, et ajuste les recommandations pour les femmes en périménopause ou ménopause.',
    desc_en: 'YUMIX integrates hormonal cycles into nutritional advice. It adapts macros based on menstrual cycle phases, optimizes insulin and cortisol management.',
    faq_fr: [
      { q: 'Comment YUMIX gère mon cycle ?', a: 'Dans les réglages cliniques, indique ta phase de vie (cycle régulier, périménopause, etc.) et la durée de ton cycle. YUMIX adaptera automatiquement : plus de glucides en phase lutéale, plus de fer après les règles, et des aliments anti-inflammatoires pendant le SPM.' },
      { q: 'C\'est utile pour les hommes aussi ?', a: 'Absolument ! YUMIX optimisé aussi la testostérone masculine via l\'alimentation : zinc (huîtres, boeuf), vitamine D, graisses saines et gestion du cortisol par le timing des repas.' },
    ],
    faq_en: [],
    action_path: '/settings',
    action_label_fr: 'Configurer mon profil hormonal',
    action_label_en: 'Configure hormonal profile',
    tag_fr: 'Hormones',
    tag_en: 'Hormones',
  },
  {
    id: 'religious',
    icon: Moon,
    gradient: 'from-amber-600/30 to-yellow-600/20',
    border: 'border-amber-500/40',
    accent: 'text-amber-400',
    bg: 'bg-amber-500/20',
    title_fr: 'Modes Religieux',
    title_en: 'Religious Modes',
    subtitle_fr: 'Ramadan, Halal, Kasher, Carême, Hindou',
    subtitle_en: 'Ramadan, Halal, Kosher, Lent, Hindu',
    desc_fr: 'YUMIX respecte tes convictions. Active ton mode culturel et YUMIX adaptera tout : les recettes, les aubaines filtrées (pas de porc si Halal), le calendrier nutritionnel (jeûne Ramadan avec repas concentrés au Iftar/Suhur), et même son langage ("Bon Iftar!", "Ramadan Moubarak!").',
    desc_en: 'YUMIX respects your beliefs. Enable your cultural mode and YUMIX adapts everything: recipes, filtered deals, nutrition calendar, and even its language.',
    faq_fr: [
      { q: 'Comment fonctionne le mode Ramadan ?', a: 'YUMIX inverse ton calendrier nutritionnel. L\'assistant ne suggérera JAMAIS de manger pendant la journée. Tous les repas sont concentrés entre l\'Iftar (coucher du soleil) et le Suhur (avant l\'aube). L\'assistant insiste sur 2-3L d\'eau pendant cette fenêtre et des repas denses en protéines.' },
      { q: 'Les aubaines sont-elles filtrées ?', a: 'Oui ! Si tu es en mode Halal, tu ne verras jamais de bacon ou de porc dans tes aubaines. Si tu es Kasher, pas de fruits de mer. Le filtre est automatique.' },
    ],
    faq_en: [],
    action_path: '/settings',
    action_label_fr: 'Choisir mon mode culturel',
    action_label_en: 'Choose cultural mode',
    tag_fr: 'Inclusif',
    tag_en: 'Inclusive',
  },
  {
    id: 'deals',
    icon: DollarSign,
    gradient: 'from-green-600/30 to-lime-600/20',
    border: 'border-green-500/40',
    accent: 'text-green-400',
    bg: 'bg-green-500/20',
    title_fr: 'Module Aubaines',
    title_en: 'Deals Module',
    subtitle_fr: 'Économies réelles sur les circulaires québécoises',
    subtitle_en: 'Real savings from Quebec grocery flyers',
    desc_fr: 'YUMIX scrute les circulaires de Maxi, IGA, Métro, Super C et Provigo chaque semaine. L\'assistant identifie les spéciaux sur les protéines, légumes et aliments clés alignés avec TES objectifs. Résultat : tu manges mieux ET tu économises.',
    desc_en: 'YUMIX scans weekly flyers from Maxi, IGA, Metro, Super C and Provigo. It finds deals on proteins and key foods aligned with YOUR goals.',
    faq_fr: [
      { q: 'Quand les aubaines sont-elles mises à jour ?', a: 'Les circulaires québécoises changent le jeudi. YUMIX met à jour les aubaines automatiquement chaque semaine.' },
      { q: 'YUMIX peut me suggérer des recettes basées sur les spéciaux ?', a: 'Oui ! Les recettes marquées d\'un badge "Aubaine" utilisent des ingrédients en spécial cette semaine. C\'est la synergie : aubaines + recettes + tes macros = économies intelligentes.' },
    ],
    faq_en: [],
    action_path: '/deals',
    action_label_fr: 'Voir les aubaines',
    action_label_en: 'See deals',
    tag_fr: 'Économies',
    tag_en: 'Savings',
  },
  {
    id: 'leaderboard',
    icon: Trophy,
    gradient: 'from-yellow-600/30 to-amber-600/20',
    border: 'border-yellow-500/40',
    accent: 'text-yellow-400',
    bg: 'bg-yellow-500/20',
    title_fr: 'Triple Threat Leaderboard',
    title_en: 'Triple Threat Leaderboard',
    subtitle_fr: 'Système de grades : RECRUE → GARDIEN → ÉLITE → LÉGENDE',
    subtitle_en: 'Grade system: RECRUIT → GUARDIAN → ELITE → LEGEND',
    desc_fr: 'Le classement se divise en TROIS categories : Discipline (constance de tes logs), Le Sommet (atteinte de tes objectifs macros) et L\'Eclaireur (badges, défis, recettes). Accumule des points dans chaque categorie pour monter en grade. Choisis un Pseudo pour te demarquer dans le classement !',
    desc_en: 'The leaderboard splits into THREE categories: Discipline (log consistency), The Summit (hitting macro targets) and The Scout (badges, challenges, recipes). Earn points in each to rank up. Choose a Pseudo to stand out!',
    faq_fr: [
      { q: 'Comment gagner des points de Constance (Discipline de Fer) ?', a: 'Chaque jour où tu logues au moins un repas te rapporte des points. Plus ton streak est long, plus tu gagnes : +10 pts/jour de streak, +50 bonus à 7 jours, +100 à 14 jours, +200 à 30 jours. La régularité est la clé !' },
      { q: 'Comment devenir GARDIEN ?', a: 'Atteins 100 points de Constance. C\'est la Discipline de Fer — prouve que tu es régulier dans tes logs quotidiens et le grade GARDIEN est à toi.' },
      { q: 'Comment devenir ÉLITE ?', a: 'Il faut une combinaison de Performance (Le Sommet ≥ 200 pts) ET d\'Engagement (L\'Éclaireur ≥ 150 pts). Atteins tes macros régulièrement et accumule badges et défis !' },
      { q: 'Comment devenir LÉGENDE EXTRA MILES ?', a: 'Le grade ultime ! Il faut ≥ 300 points dans les TROIS catégories. Seuls les plus complets atteignent ce niveau. ALL IN ALL TIME !!!' },
      { q: 'Qu\'est-ce que le Pseudo ?', a: 'C\'est ton nom de code dans le leaderboard ! Va dans la page Triple Threat et clique sur "Choisis ton pseudo" pour te démarquer. Les pseudos sont uniques — premier arrivé, premier servi.' },
    ],
    faq_en: [
      { q: 'How to earn Constancy points?', a: 'Each day you log at least one meal earns points. Longer streaks = more points: +10 pts/streak day, +50 bonus at 7 days, +100 at 14, +200 at 30.' },
      { q: 'How to become GUARDIAN?', a: 'Reach 100 Constancy points through consistent daily logging.' },
      { q: 'How to become ELITE?', a: 'Combine Performance (≥200 pts) AND Engagement (≥150 pts). Hit your macros and earn badges!' },
    ],
    action_path: '/leaderboard',
    action_label_fr: 'Voir le classement',
    action_label_en: 'See leaderboard',
    tag_fr: 'Nouveau',
    tag_en: 'New',
  },
  {
    id: 'adaptive',
    icon: Zap,
    gradient: 'from-red-600/30 to-orange-600/20',
    border: 'border-red-500/40',
    accent: 'text-red-400',
    bg: 'bg-red-500/20',
    title_fr: 'Défis de Correction Tactique',
    title_en: 'Tactical Correction Challenges',
    subtitle_fr: "L'IA détecte ta faiblesse et te lance un défi ciblé",
    subtitle_en: 'The AI detects your weakness and launches a targeted challenge',
    desc_fr: "Chaque dimanche soir, l'algorithme analyse tes 3 catégories (Discipline, Sommet, Éclaireur) et identifie ton point faible. Le lundi matin, tu reçois une mission personnalisée avec des POINTS x2 dans ta catégorie faible. Trois missions possibles : Opération Blackout Zéro (constance), Le Mile de Plus (performance + workout), Transmission Radio (engagement).",
    desc_en: 'Every Sunday, the algorithm analyzes your 3 categories and identifies your weakness. Monday morning, you receive a personalized mission with x2 POINTS in your weak category.',
    faq_fr: [
      { q: "C'est quoi Opération Blackout Zéro ?", a: "Mission pour booster ta Discipline de Fer. Objectif : loguer au moins 2 repas par jour pendant 3 jours consécutifs. Si tu réussis, tu gagnes x2 points de Constance !" },
      { q: "C'est quoi Le Mile de Plus ?", a: "Mission pour booster Le Sommet. Objectif : atteindre tes objectifs protéines ET loguer un entraînement pendant 3 jours. Points Performance x2 à la clé." },
      { q: "C'est quoi Transmission Radio ?", a: "Mission pour booster L'Éclaireur. Objectif : créer 1 recette, loguer 1 workout et partager 1 badge cette semaine. Points Engagement x2 garantis." },
      { q: "Comment les défis sont générés ?", a: "L'algorithme compare tes scores dans les 3 catégories chaque dimanche soir. La catégorie la plus basse reçoit automatiquement un défi ciblé. Tu as 7 jours pour le compléter." },
      { q: "Je peux lancer un défi manuellement ?", a: "Oui ! Sur le Dashboard ou la page Leaderboard, clique sur 'Lancer une mission' quand tu n'as pas de défi actif." },
    ],
    faq_en: [
      { q: 'What is Operation Blackout Zéro?', a: 'Mission to boost Iron Discipline. Log 2+ meals/day for 3 days. x2 Constancy points.' },
      { q: 'What is The Extra Mile?', a: 'Mission to boost The Summit. Hit protein + workout for 3 days. x2 Performance points.' },
      { q: 'What is Radio Transmission?', a: 'Mission to boost The Scout. 1 recipe + 1 workout + 1 badge share. x2 Engagement points.' },
    ],
    action_path: '/leaderboard',
    action_label_fr: 'Voir ma mission',
    action_label_en: 'See my mission',
    tag_fr: 'Nouveau',
    tag_en: 'New',
  },
  {
    id: 'voice',
    icon: Mic,
    gradient: 'from-orange-600/30 to-red-600/20',
    border: 'border-orange-500/40',
    accent: 'text-orange-400',
    bg: 'bg-orange-500/20',
    title_fr: 'Coach Vocal',
    title_en: 'Voice Coach',
    subtitle_fr: 'Parle a YUMIX comme a un vrai coach',
    subtitle_en: 'Talk to YUMIX like a real coach',
    desc_fr: 'Active le mode vocal et parle directement a l\'assistant YUMIX. Il écoute ta question, analyse avec son intelligence artificielle, et te répond a voix haute en français. C\'est comme avoir un nutritionniste dans ta poche, disponible 24/7.',
    desc_en: 'Enable voice mode and speak directly to the YUMIX assistant. It listens, analyzes with AI, and responds out loud in your language.',
    faq_fr: [
      { q: 'Comment utiliser le coach vocal ?', a: 'Va sur la page Assistant YUMIX, active le "Mode Voix" (bouton orange), puis appuie sur le micro. Parle naturellement, comme si tu parlais a ton coach. YUMIX te répondra a voix haute.' },
      { q: 'Est-ce que YUMIX comprend le québécois ?', a: 'Oui ! Le moteur de reconnaissance vocale est configuré en français canadien (fr-CA). YUMIX comprend les expressions locales.' },
    ],
    faq_en: [],
    action_path: '/coach-ai',
    action_label_fr: 'Parler a l\'assistant YUMIX',
    action_label_en: 'Talk to YUMIX assistant',
    tag_fr: 'IA Vocale',
    tag_en: 'Voice AI',
  },
  {
    id: 'family',
    icon: Users,
    gradient: 'from-blue-600/30 to-indigo-600/20',
    border: 'border-blue-500/40',
    accent: 'text-blue-400',
    bg: 'bg-blue-500/20',
    title_fr: 'Profil Familial',
    title_en: 'Family Profile',
    subtitle_fr: 'Calcul des portions pour toute la famille',
    subtitle_en: 'Portion calculation for the whole family',
    desc_fr: 'On ne mange pas seul ! Configure le nombre d\'adultes et d\'enfants dans ton foyer. YUMIX ajustera automatiquement les quantités des recettes, la liste d\'épicerie et les portions pour que toute la famille mange bien selon ses besoins.',
    desc_en: 'You don\'t eat alone! Configure adults and children in your household. YUMIX adjusts recipes, grocery lists and portions for the whole family.',
    faq_fr: [
      { q: 'Comment nourrir ma famille avec mes macros ?', a: 'Dans les réglages, ajoute chaque membre de ta famille avec son âge et ses allergies. YUMIX calculera les portions : un papa athlète de 200lbs, une maman, et deux enfants n\'ont pas les mêmes besoins. Les recettes s\'adapteront automatiquement.' },
    ],
    faq_en: [],
    action_path: '/settings',
    action_label_fr: 'Configurer ma famille',
    action_label_en: 'Configure family',
    tag_fr: 'Famille',
    tag_en: 'Family',
  },
  {
    id: 'recipes',
    icon: BookOpen,
    gradient: 'from-violet-600/30 to-purple-600/20',
    border: 'border-violet-500/40',
    accent: 'text-violet-400',
    bg: 'bg-violet-500/20',
    title_fr: 'Livre de Recettes YUMIX',
    title_en: 'YUMIX Recipe Book',
    subtitle_fr: 'Recettes personnalisées selon tes croyances et objectifs',
    subtitle_en: 'Recipes personalized to your beliefs and goals',
    desc_fr: 'Ton livre de recettes dynamique ne contient QUE ce que tu aimes, respectant tes croyances (Halal, sans porc) et tes besoins (hormonal, clinique). YUMIX génère des idées basées sur les aubaines de la semaine et les enregistré dans ton livre personnel.',
    desc_en: 'Your dynamic recipe book contains ONLY what you like, respecting your beliefs and needs. YUMIX generates ideas based on weekly deals.',
    faq_fr: [
      { q: 'Où sont mes recettes ?', a: 'Dans la section "Recettes" du menu. YUMIX génère des idées basées sur les spéciaux de la semaine (badge "Aubaine"), tes préférences religieuses (filtrage automatique) et tes objectifs protéinés. Tu peux aussi chercher sur le web et sauvegarder tes favorites.' },
    ],
    faq_en: [],
    action_path: '/recipes',
    action_label_fr: 'Ouvrir mon livre de recettes',
    action_label_en: 'Open my recipe book',
    tag_fr: 'Recettes',
    tag_en: 'Recipes',
  },
  {
    id: 'calendar',
    icon: CalendarDays,
    gradient: 'from-cyan-600/30 to-sky-600/20',
    border: 'border-cyan-500/40',
    accent: 'text-cyan-400',
    bg: 'bg-cyan-500/20',
    title_fr: 'Agenda Connecté',
    title_en: 'Connected Calendar',
    subtitle_fr: 'Planification des repas dans ton agenda',
    subtitle_en: 'Meal planning in your calendar',
    desc_fr: 'C\'est le chaînon manquant ! YUMIX inscrit les repas directement dans ton agenda (Google Calendar/Apple). L\'assistant peut bloquer des plages horaires pour le "Meal Prep" du dimanche et inscrire le menu du soir dans l\'agenda familial.',
    desc_en: 'The missing link! YUMIX adds meals directly to your calendar. It can block meal prep time and add dinner plans to your family calendar.',
    faq_fr: [
      { q: 'Comment planifier ma semaine ?', a: 'Utilise le Planning dans le menu pour créer ton plan de repas, puis synchronise avec ton agenda. YUMIX peut aussi te suggérer un plan basé sur les aubaines et tes macros.' },
    ],
    faq_en: [],
    action_path: '/planner',
    action_label_fr: 'Voir le planning',
    action_label_en: 'See planner',
    tag_fr: 'Bientôt',
    tag_en: 'Coming soon',
  },
  {
    id: 'neuro',
    icon: ShieldCheck,
    gradient: 'from-purple-600/30 to-fuchsia-600/20',
    border: 'border-purple-500/40',
    accent: 'text-purple-400',
    bg: 'bg-purple-500/20',
    title_fr: 'Santé Cognitive & Neuro-Soutien',
    title_en: 'Cognitive Health & Neuro-Support',
    subtitle_fr: 'Nutrition neuro-protectrice pour les aînés et la clarté mentale',
    subtitle_en: 'Neuroprotective nutrition for seniors and mental clarity',
    desc_fr: 'Ce module utilise la science de la nutrition pour soutenir la fonction cérébrale. YUMIX priorise les oméga-3 (DHA), les antioxydants (bleuets, curcuma), les graisses saines (avocat, noix) et le régime MIND — prouvé pour réduire le risque de déclin cognitif de 53%. Les repas sont adaptés en texture et en fréquence pour prévenir la dénutrition.',
    desc_en: 'This module uses nutrition science to support brain function. YUMIX prioritizes omega-3s, antioxidants, healthy fats and the MIND diet — proven to reduce cognitive decline risk by 53%.',
    faq_fr: [
      { q: 'Pour qui est ce mode ?', a: 'Pour les aînés souhaitant protéger leur santé cognitive, les personnes atteintes d\'Alzheimer ou de troubles cognitifs, ou simplement ceux qui veulent optimiser leur clarté mentale à tout âge.' },
      { q: 'Qu\'est-ce que le régime MIND ?', a: 'Le régime MIND combine le méditerranéen et le DASH. Il priorise : 6+ portions de légumes verts/semaine, bleuets, noix, poisson 2x/semaine, huile d\'olive. Réduction de 53% du risque d\'Alzheimer.' },
      { q: 'Quels nutriments sont prioritaires ?', a: 'DHA/EPA (saumon, sardines), antioxydants (bleuets, curcuma), choline (oeufs), vitamine B12, vitamine D, magnésium (noix, chocolat noir).' },
    ],
    faq_en: [
      { q: 'Who is this mode for?', a: 'For seniors wanting to protect cognitive health, those with Alzheimer, or anyone wanting to optimize mental clarity.' },
    ],
    action_path: '/settings',
    action_label_fr: 'Activer le mode Neuro-Soutien',
    action_label_en: 'Enable Neuro-Support mode',
    tag_fr: 'Aînés',
    tag_en: 'Seniors',
  },
];

const container = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const item = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };

const KnowledgeBase = () => {
  const { language, t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fr = language === 'fr';

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12 max-w-5xl mx-auto" data-testid="knowledge-base">
        {/* Hero */}
        <div className="mb-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/15 border border-primary/30 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-xs font-bold text-primary uppercase tracking-widest">
              {t('pages.kb_guide')}
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-foreground mb-3">
            {t('pages.kb_subtitle')}
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base max-w-2xl mx-auto">
            {fr
              ? 'Chaque module est une extension Pro que tu actives selon tes besoins. Du magasinage à l\'assiette, en passant par l\'agenda — automatisation complète.'
              : 'Each module is a Pro extension you activate based on your needs. From shopping to plate, through calendar — full automation.'}
          </p>
        </div>

        {/* Module Cards */}
        <motion.div 
          className="space-y-6"
          variants={container}
          initial="hidden"
          animate="show"
        >
          {MODULES.map((mod) => (
            <motion.div 
              key={mod.id} 
              variants={item}
              className={`relative bg-gradient-to-br ${mod.gradient} border ${mod.border} rounded-xl overflow-hidden`}
              data-testid={`module-card-${mod.id}`}
            >
              {/* Tag */}
              <div className="absolute top-3 right-3">
                <span className={`px-2.5 py-1 ${mod.bg} ${mod.accent} text-xs font-bold uppercase tracking-wider rounded-full`}>
                  {fr ? mod.tag_fr : mod.tag_en}
                </span>
              </div>

              <div className="p-5 sm:p-6">
                {/* Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-12 h-12 ${mod.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <mod.icon className={`w-6 h-6 ${mod.accent}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="font-heading text-lg sm:text-xl uppercase tracking-tight text-foreground font-bold">
                      {fr ? mod.title_fr : mod.title_en}
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                      {fr ? mod.subtitle_fr : mod.subtitle_en}
                    </p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-foreground/80 leading-relaxed mb-4">
                  {fr ? mod.desc_fr : mod.desc_en}
                </p>

                {/* FAQ */}
                {((fr ? mod.faq_fr : mod.faq_en) || []).length > 0 && (
                  <div className="space-y-3 mb-5">
                    {(fr ? mod.faq_fr : mod.faq_en).map((faq, i) => (
                      <details key={i} className="group bg-background/30 rounded-lg border border-border/50">
                        <summary className="flex items-center gap-2 cursor-pointer p-3 text-sm font-medium text-foreground hover:text-primary transition-colors">
                          <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform group-open:rotate-90" />
                          {faq.q}
                        </summary>
                        <div className="px-3 pb-3 pl-9 text-sm text-muted-foreground leading-relaxed">
                          {faq.a}
                        </div>
                      </details>
                    ))}
                  </div>
                )}

                {/* Action Button */}
                <Button
                  data-testid={`activate-${mod.id}`}
                  onClick={() => navigate(mod.action_path)}
                  className={`w-full sm:w-auto ${mod.bg} hover:opacity-80 ${mod.accent} border ${mod.border} font-bold`}
                  variant="outline"
                >
                  {fr ? mod.action_label_fr : mod.action_label_en}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <div className="mt-10 text-center">
          <div className="bg-gradient-to-r from-primary/15 to-accent/15 border border-primary/30 rounded-xl p-6">
            <Zap className="w-8 h-8 text-primary mx-auto mb-3" />
            <h3 className="font-heading text-lg uppercase tracking-tight font-bold mb-2">
              {fr ? 'Prêt à passer au niveau supérieur ?' : 'Ready to level up?'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
              {fr
                ? 'Active les modules qui te correspondent et laisse YUMIX faire le reste. L\'IA s\'adapté à TOI, pas l\'inverse.'
                : 'Enable the modules that fit you and let YUMIX do the rest. AI adapts to YOU, not the other way around.'}
            </p>
            <Button
              data-testid="go-to-coach"
              onClick={() => navigate('/coach-ai')}
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold"
            >
              <Mic className="w-4 h-4 mr-2" />
              {fr ? 'Parler a l\'assistant YUMIX' : 'Talk to YUMIX assistant'}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default KnowledgeBase;
